// Client-side copy of the public state (Req 2.5-2.7, 2.9, 1.8). No DOM: it applies
// snapshots (GET /api/state and the `hello` message) and realtime messages
// (realtime/publicFeed.ts) and reports which parts of the page need redrawing.
//
// A section the server marks {available:false} becomes null here, so the page shows its
// "currently unavailable" text; everything else is kept. Every message or snapshot that
// carries serverTime re-syncs the clock offset used by the countdown (Req 10.5).
import type { CameraLike, CountdownLike, ZoneLike } from './logic.js';

export interface CommentLike {
  readonly name: string;
  readonly message: string;
  readonly at: number;
}

export interface HomeState {
  /** serverTime - device time, in ms. */
  offsetMs: number;
  zones: ZoneLike[] | null;
  controlsLive: boolean | null;
  /** HA_Link connected; null = unknown. */
  reachable: boolean | null;
  countdown: CountdownLike | null;
  cameras: CameraLike[] | null;
  cameraIntervalS: number;
  visitors: number | null;
  togglesToday: number | null;
  overloadsToday: number | null;
  laptopOpen: boolean | null;
  /** undefined = section unavailable; null = no comments yet. */
  comment: CommentLike | null | undefined;
  autoplayRunning: boolean;
  showRunning: boolean;
}

export type Part =
  'zones' | 'countdown' | 'cameras' | 'visitors' | 'stats' | 'laptop' | 'comment' | 'notices';

export const ALL_PARTS: readonly Part[] = Object.freeze([
  'zones',
  'countdown',
  'cameras',
  'visitors',
  'stats',
  'laptop',
  'comment',
  'notices',
]);

export function emptyState(): HomeState {
  return {
    offsetMs: 0,
    zones: null,
    controlsLive: null,
    reachable: null,
    countdown: null,
    cameras: null,
    cameraIntervalS: 2,
    visitors: null,
    togglesToday: null,
    overloadsToday: null,
    laptopOpen: null,
    comment: undefined,
    autoplayRunning: false,
    showRunning: false,
  };
}

type Obj = Record<string, unknown>;

const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const bool = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null);
const str = (v: unknown, d = ''): string => (typeof v === 'string' ? v : d);

/** The section's fields when available, else null. */
function sectionOf(snap: Obj, key: string): Obj | null {
  const s = snap[key];
  return isObj(s) && s.available === true ? s : null;
}

function parseZone(v: unknown): ZoneLike | null {
  if (!isObj(v)) return null;
  const n = num(v.n);
  if (n === null || n < 1 || n > 8) return null;
  return {
    n,
    name: str(v.name, 'Zone ' + String(n)),
    description: str(v.description),
    requested: v.requested === 'ON' ? 'ON' : 'OFF',
    reported: str(v.reported, 'unknown'),
    pending: v.pending === true,
  };
}

function parseCamera(v: unknown): CameraLike | null {
  if (!isObj(v)) return null;
  const n = num(v.n);
  if (n === null || n < 1 || n > 3) return null;
  return {
    n,
    name: str(v.name, 'Camera ' + String(n)),
    status: v.status === 'LIVE' ? 'LIVE' : 'OFFLINE',
    ...(typeof v.reason === 'string' ? { reason: v.reason } : {}),
  };
}

function parseCountdown(v: Obj): CountdownLike | null {
  if (v.state !== 'ONLINE' && v.state !== 'OFFLINE') return null;
  const endsAt = num(v.endsAt);
  const startsAt = num(v.startsAt);
  return {
    state: v.state,
    kind: str(v.kind),
    label: str(v.label),
    ...(endsAt !== null ? { endsAt } : {}),
    ...(startsAt !== null ? { startsAt } : {}),
  };
}

function parseComment(v: unknown): CommentLike | null {
  if (!isObj(v)) return null;
  const at = num(v.at);
  return at === null ? null : { name: str(v.name), message: str(v.message), at };
}

function syncClock(state: HomeState, msg: Obj, deviceNow: number): void {
  const t = num(msg.serverTime);
  if (t !== null) state.offsetMs = t - deviceNow;
}

/** Replaces every displayed value with a full snapshot (Req 2.5). */
export function applySnapshot(state: HomeState, snap: unknown, deviceNow: number): Set<Part> {
  if (!isObj(snap)) return new Set();
  syncClock(state, snap, deviceNow);
  const zones = sectionOf(snap, 'zones');
  state.zones =
    zones && Array.isArray(zones.zones)
      ? zones.zones.map(parseZone).filter((z): z is ZoneLike => z !== null)
      : null;
  state.controlsLive = bool(sectionOf(snap, 'controlsLive')?.value);
  state.reachable = bool(sectionOf(snap, 'lightsReachable')?.value);
  const cd = sectionOf(snap, 'countdown');
  state.countdown = cd ? parseCountdown(cd) : null;
  const cams = sectionOf(snap, 'cameras');
  state.cameras =
    cams && Array.isArray(cams.cameras)
      ? cams.cameras.map(parseCamera).filter((c): c is CameraLike => c !== null)
      : null;
  const interval = num(snap.cameraIntervalS);
  if (interval !== null) state.cameraIntervalS = interval;
  state.visitors = num(sectionOf(snap, 'visitors')?.count);
  const stats = sectionOf(snap, 'stats');
  state.togglesToday = num(stats?.togglesToday);
  state.overloadsToday = num(stats?.overloadsToday);
  state.laptopOpen = bool(sectionOf(snap, 'laptopForm')?.open);
  const lc = sectionOf(snap, 'latestComment');
  state.comment = lc ? parseComment(lc.comment) : undefined;
  state.autoplayRunning = sectionOf(snap, 'autoplayRunning')?.value === true;
  state.showRunning = sectionOf(snap, 'showRunning')?.value === true;
  return new Set(ALL_PARTS);
}

/** Applies one realtime message; returns the parts to redraw. */
export function applyMessage(state: HomeState, msg: Obj, deviceNow: number): Set<Part> {
  syncClock(state, msg, deviceNow);
  const parts = new Set<Part>();
  switch (msg.type) {
    case 'hello':
      return applySnapshot(state, msg, deviceNow);
    case 'zone': {
      const z = parseZone(msg);
      if (!z) break;
      // Keep name/description from zoneMeta; the zone message carries them too.
      const list = state.zones ?? [];
      const i = list.findIndex((x) => x.n === z.n);
      if (i >= 0) {
        list[i] = z;
      } else {
        list.push(z);
        list.sort((a, b) => a.n - b.n);
      }
      state.zones = list;
      parts.add('zones');
      break;
    }
    case 'zoneMeta': {
      if (!Array.isArray(msg.zones) || !state.zones) break;
      for (const m of msg.zones) {
        if (!isObj(m)) continue;
        const z = state.zones.find((x) => x.n === m.n);
        if (z) Object.assign(z, { name: str(m.name, z.name), description: str(m.description) });
      }
      parts.add('zones');
      break;
    }
    case 'modes': {
      const live = bool(msg.controlsLive);
      if (live !== null) state.controlsLive = live;
      parts.add('zones').add('notices');
      break;
    }
    case 'countdown': {
      const c = parseCountdown(msg);
      if (c) state.countdown = c;
      parts.add('countdown');
      break;
    }
    case 'camera': {
      const c = parseCamera(msg);
      if (!c) break;
      const list = state.cameras ?? [];
      const i = list.findIndex((x) => x.n === c.n);
      if (i >= 0) {
        list[i] = c;
      } else {
        list.push(c);
        list.sort((a, b) => a.n - b.n);
      }
      state.cameras = list;
      parts.add('cameras');
      break;
    }
    case 'cameraInterval': {
      const s = num(msg.intervalS);
      if (s !== null) state.cameraIntervalS = s;
      parts.add('cameras');
      break;
    }
    case 'stats': {
      const v = num(msg.visitors);
      if (v !== null) state.visitors = v;
      // null from the server means "unavailable" (Req 19.7), never shown as 0.
      state.togglesToday = num(msg.togglesToday);
      state.overloadsToday = num(msg.overloadsToday);
      parts.add('visitors').add('stats');
      break;
    }
    case 'laptopForm': {
      const open = bool(msg.open);
      if (open !== null) state.laptopOpen = open;
      parts.add('laptop');
      break;
    }
    case 'latestComment':
      state.comment = msg.comment === null ? null : (parseComment(msg.comment) ?? state.comment);
      parts.add('comment');
      break;
    case 'autoplay':
      state.autoplayRunning = msg.running === true;
      parts.add('notices');
      break;
    case 'show':
      state.showRunning = msg.running === true;
      parts.add('notices').add('zones');
      break;
    case 'reachable': {
      const r = bool(msg.value);
      if (r !== null) state.reachable = r;
      parts.add('notices').add('zones');
      break;
    }
    default:
      break;
  }
  return parts;
}

/** Server time now, from the device clock and the last offset. */
export function serverNow(state: HomeState, deviceNow: number): number {
  return deviceNow + state.offsetMs;
}
